import { LoadingStatus } from '../Components/ProgressViewer/ProgressViewer';
import { BlueprintFlowData } from '../types/flowScenario';
import React from 'react';
import { getFlowDetails } from '../utils/getFlowDetails';
import { normalizeFeedbackDetails } from '../utils/normalizeFeedbackDetails';

export type FlowDataProviderProps = {
  flowData: BlueprintFlowData | undefined;
  setFlowData: (flowData: BlueprintFlowData | undefined) => void;
  retriggerFlowDataFetch: () => void;
  loadingStatus: LoadingStatus;
};

export const FlowDataContext = React.createContext<FlowDataProviderProps | undefined>(undefined);

export const useFlowData = (): BlueprintFlowData | undefined => {
  const context = React.useContext(FlowDataContext);
  if (!context) {
    throw new Error('useFlowData must be used within a FlowDataProvider');
  }
  return context.flowData;
};

export const useLoadingStatus = (): LoadingStatus => {
  const context = React.useContext(FlowDataContext);
  if (!context) {
    throw new Error('useLoadingStatus must be used within a FlowDataProvider');
  }
  return context.loadingStatus;
};

export const useSetFlowData = (): ((flowData: BlueprintFlowData | undefined) => void) => {
  const context = React.useContext(FlowDataContext);
  if (!context) {
    throw new Error('useSetFlowData must be used within a FlowDataProvider');
  }
  return context.setFlowData;
};

export const useRetriggerFlowDataFetch = (): (() => void) => {
  const context = React.useContext(FlowDataContext);
  if (!context) {
    throw new Error('useRetriggerFlowDataFetch must be used within a FlowDataProvider');
  }
  return context.retriggerFlowDataFetch;
};

export function useFlowContextValue(): FlowDataProviderProps {
  const [flowData, setFlowData] = React.useState<BlueprintFlowData | undefined>(undefined);
  const [loadingStatus, setLoadingStatus] = React.useState<LoadingStatus>('pending');

  const normalizeAndSetFlowData = React.useCallback((data: BlueprintFlowData) => {
    // Normalize feedbackDetails to ensure it's always in array format internally
    if (data.feedbackDetails && !Array.isArray(data.feedbackDetails)) {
      const { allFollowUps } = normalizeFeedbackDetails(data.feedbackDetails);
      data.feedbackDetails = allFollowUps;
    }
    setFlowData(data);
  }, []);

  const retriggerFlowDataFetch = React.useCallback(() => {
    setLoadingStatus('pending');
    setFlowData(undefined);
    getFlowDetails('blueprint')
      .then((data) => {
        setLoadingStatus('resolved');
        normalizeAndSetFlowData(data);
        console.log(data);
      })
      .catch((error) => {
        setLoadingStatus('rejected');
        setFlowData(undefined);
        console.error(error);
      });
  }, [normalizeAndSetFlowData]);

  React.useEffect(() => {
    setLoadingStatus('pending');
    setFlowData(undefined);
    getFlowDetails('blueprint')
      .then((data) => {
        setLoadingStatus('resolved');
        normalizeAndSetFlowData(data);
        console.log(data);
      })
      .catch((error) => {
        setLoadingStatus('rejected');
        setFlowData(undefined);
        console.error(error);
      });
  }, [normalizeAndSetFlowData]);

  return {
    flowData,
    setFlowData,
    loadingStatus,
    retriggerFlowDataFetch,
  };
}

export const FlowDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const value = useFlowContextValue();
  return <FlowDataContext.Provider value={value}>{children}</FlowDataContext.Provider>;
};
